import * as THREE from 'three';

const MAX = 26;
const RANGE = 320; // metres
const BOARD = { quarter: '#f7eed9', village: '#f7eed9', town: '#f7eed9' };
const KIND_COLOR = {
  home: '#f3a6c4', hospital: '#e5484d', clinic: '#e5484d', market: '#f2b705', fuel: '#f08a24', bus: '#3b9eff', school: '#b9a8ff', university: '#b9a8ff',
  college: '#b9a8ff', worship: '#f7eed9', bank: '#7fd08f', hotel: '#f3a6c4', police: '#7fa6ff', airport: '#f7eed9', stadium: '#7fd08f',
};
// small things only show up close; quarters and big landmarks from further off
const REACH = { home: 400, quarter: 900, village: 1400, town: 2500, airport: 1500, hospital: 500, university: 600, market: 500, stadium: 500 };

function makeSprite(text, kind) {
  const pad = 14;
  const c = document.createElement('canvas');
  const g = c.getContext('2d');
  const font = '600 34px "Barlow Semi Condensed", Arial, sans-serif';
  g.font = font;
  const w = Math.ceil(g.measureText(text).width) + pad * 2;
  c.width = w;
  c.height = 58;
  const board = BOARD[kind];
  g.fillStyle = board || 'rgba(27,30,35,0.86)';
  g.fillRect(0, 0, w, 50);
  g.fillStyle = board ? '#f2b705' : KIND_COLOR[kind] || '#c9c0ad';
  g.fillRect(0, 50, w, 8);
  g.font = font;
  g.textBaseline = 'middle';
  g.fillStyle = board ? '#1b1e23' : '#f7eed9';
  g.fillText(text, pad, 26);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: true, fog: false, transparent: true }));
  sprite.userData.aspect = w / 58;
  return sprite;
}

// Name boards that float over the named places near the player.
export class Labels {
  constructor(scene, places, heightmap) {
    this.scene = scene;
    this.places = places;
    this.heightmap = heightmap;
    this.active = new Map(); // place -> sprite
    this.at = { x: Infinity, z: Infinity };
    this.keep = new Set();
    this.pending = []; // boards still to be made, a few per frame so no frame draws them all
  }

  // Call often; it only re-picks the labels after the player has moved a little.
  update(x, z, camera) {
    if (Math.abs(x - this.at.x) > 25 || Math.abs(z - this.at.z) > 25) {
      this.at = { x, z };
      const near = [];
      for (const p of this.places) {
        const reach = REACH[p.k] || RANGE;
        const d = Math.hypot(p.x - x, p.z - z);
        if (d < reach) near.push({ p, score: d / reach });
      }
      near.sort((a, b) => a.score - b.score);
      const keep = new Set(near.slice(0, MAX).map((n) => n.p));
      this.keep = keep;
      for (const [p, s] of this.active) {
        if (keep.has(p)) continue;
        this.scene.remove(s);
        s.material.map.dispose();
        s.material.dispose();
        this.active.delete(p);
      }
      this.pending = [...keep].filter((p) => !this.active.has(p));
    }
    for (let made = 0; made < 3 && this.pending.length; ) {
      const p = this.pending.shift();
      if (!this.keep.has(p) || this.active.has(p)) continue;
      const s = makeSprite(p.n, p.k);
      const settlement = p.k === 'quarter' || p.k === 'village' || p.k === 'town';
      s.position.set(p.x, this.heightmap.heightAt(p.x, p.z) + (settlement ? 26 : 11), p.z);
      this.scene.add(s);
      this.active.set(p, s);
      made++;
    }
    // keep each board a steady size on screen, within limits
    for (const s of this.active.values()) {
      const d = s.position.distanceTo(camera.position);
      const h = Math.max(1.1, Math.min(40, d * 0.035));
      s.scale.set(h * s.userData.aspect, h, 1);
    }
  }
}
